import { Editor, Transforms, Descendant, Range } from 'slate';
import { ReactEditor } from 'slate-react';
import { HistoryEditor } from 'slate-history';
import { htmlToSlate, slateToHtml } from './SlateSerialisationUtil';

// Validates Slate nodes structure and fixes common issues
function validateSlateNodes(nodes: any[]): any[] | null {
    if (!Array.isArray(nodes)) return null;

    try {
        const validate = (node: any): any => {
            if (!node) return null;

            if (node.text !== undefined) {
                return { text: String(node.text || ''), ...node };
            }

            if (!node.type || !Array.isArray(node.children)) return null;

            const children = node.children.map(validate).filter(Boolean);
            return { ...node, children: children.length ? children : [{ text: '' }] };
        };

        return nodes.map(validate).filter(Boolean);
    } catch {
        return null;
    }
}

/**
 * Enhanced plugin that handles pasting HTML content with full formatting support
 * from external sources (Gmail, Google Docs, MS Word, etc.)
 * Also handles copying out with HTML formatting preserved.
 */
function withHtmlPaste<T extends Editor>(editor: T): T & ReactEditor & HistoryEditor {
    const e = editor as T & ReactEditor & HistoryEditor;
    const { insertData, setFragmentData } = e;

    e.insertData = (data: DataTransfer) => {
        const html = data.getData('text/html');
        const plainText = data.getData('text/plain');

        // Check if HTML contains a table - if so, skip and let withExcelPaste handle it
        if (html?.includes('<table')) {
            insertData(data);
            return;
        }

        if (html?.trim()) {
            try {
                const parsed = htmlToSlate(html);
                if (!parsed?.length) throw new Error('Empty parse');

                const validated = validateSlateNodes(parsed.map(cleanFontFamily));
                if (!validated?.length) throw new Error('Invalid structure');

                const hasContent = validated.some(
                    (n) => n.type !== 'paragraph' || n.children?.some((c) => c.type === 'link' || c.text?.trim()),
                );

                if (hasContent && e.selection) {
                    if (!Range.isCollapsed(e.selection)) Transforms.delete(e);

                    const isSingle = validated.length === 1 && validated[0].type === 'paragraph';
                    const hasLists = validated.some((n) => ['unorderedList', 'orderedList'].includes(n.type));

                    Editor.withoutNormalizing(e, () => {
                        if (isSingle && validated[0].children?.length) {
                            Transforms.insertFragment(e, validated[0].children as Descendant[]);
                        } else if (hasLists || validated.filter((n) => n.type === 'paragraph').length > 1) {
                            Transforms.insertNodes(e, validated);
                        } else {
                            Transforms.insertNodes(e, validated);
                        }
                    });
                    return;
                }
            } catch (error) {
                // Silently fall through to plain text - expected for complex content like tables
                console.warn('HTML paste failed, using plain text:', error);
            }
        }

        // Try plain text before default behavior
        if (plainText?.trim()) {
            e.insertText(plainText);
            return;
        }

        insertData(data);
    };

    e.setFragmentData = (data: DataTransfer, originEvent?: 'drag' | 'copy' | 'cut') => {
        setFragmentData(data, originEvent);

        try {
            const { selection } = e;
            if (!selection) return;

            const fragment = Editor.fragment(e, selection);
            if (fragment?.length) {
                const html = slateToHtml(fragment);
                if (html) data.setData('text/html', html);

                const plainText = fragment.map((n) => Node.string(n)).join('\n');
                data.setData('text/plain', plainText);
            }
        } catch (error) {
            console.error('setFragmentData error:', error);
        }
    };

    return e;
}

const Node = {
    string: (node: any): string => {
        if (typeof node.text === 'string') {
            return node.text;
        }
        if (node.children && Array.isArray(node.children)) {
            return node.children.map((child: any) => Node.string(child)).join('');
        }
        return '';
    },
};

const cleanFontFamily = (node: any): any => {
    if (!node) return node;
    try {
        if (node.text !== undefined) {
            const { fontFamily, fontSize, textAlign, ...rest } = node;
            return rest;
        }
        if (Array.isArray(node.children)) {
            return { ...node, children: node.children.map(cleanFontFamily) };
        }
        return node;
    } catch {
        return node;
    }
};

export default withHtmlPaste;
